import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import API from "../services/api";
import { formatDateDMY, formatDateTimeDMY } from "../utils/dateFormat";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function todayISO() {
  return toISODate(new Date());
}

function startOfMonthISO() {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function startOfYearISO() {
  const d = new Date();
  return toISODate(new Date(d.getFullYear(), 0, 1));
}

const PRESETS = {
  all: { label: "Todo el historial", start: "", end: "" },
  this_month: { label: "Este mes", getRange: () => ({ start: startOfMonthISO(), end: todayISO() }) },
  this_year: { label: "Este año (YTD)", getRange: () => ({ start: startOfYearISO(), end: todayISO() }) },
  custom: { label: "Rango personalizado", start: null, end: null }
};

/** Params de API; si no hay fechas, solo máquina (o vacío). */
function buildQueryParams(start, end, machineId) {
  const p = {};
  if (machineId) p.machine_id = machineId;
  if (start && end) {
    p.start = start;
    p.end = end;
  }
  return p;
}

function Dashboard() {
  const [kpi, setKpi] = useState({});
  const [daily, setDaily] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [byMachine, setByMachine] = useState([]);
  const [machineRankings, setMachineRankings] = useState({ top10: [], bottom10: [] });
  const [machines, setMachines] = useState([]);
  const [devices, setDevices] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [ytdCompare, setYtdCompare] = useState(null);

  const [preset, setPreset] = useState("this_year");
  const [startDate, setStartDate] = useState(() => startOfYearISO());
  const [endDate, setEndDate] = useState(() => todayISO());
  const [selectedMachine, setSelectedMachine] = useState("");
  const [showYearCompare, setShowYearCompare] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const firstFetchDoneRef = useRef(false);

  const loadMachines = async () => {
    const res = await API.get("/machines");
    setMachines(Array.isArray(res.data) ? res.data : []);
  };

  const queryParams = useCallback(() => {
    const p = {};
    if (selectedMachine) p.machine_id = selectedMachine;
    if (startDate && endDate) {
      p.start = startDate;
      p.end = endDate;
    }
    return p;
  }, [startDate, endDate, selectedMachine]);

  const loadAll = async (compareOverride, qpOverride) => {
    const afterFirst = firstFetchDoneRef.current;
    if (afterFirst) setRefreshing(true);
    const qp = qpOverride !== undefined ? qpOverride : queryParams();
    const wantCompare =
      compareOverride !== undefined ? compareOverride : showYearCompare;

    try {
      const [kpiRes, dailyRes, monthlyRes, machineRes, lowStockRes, rankRes, devicesRes] =
        await Promise.all([
          API.get("/sales/kpi-filter", { params: qp }),
          API.get("/sales/summary/daily", { params: qp }),
          API.get("/sales/summary/monthly", { params: qp }),
          API.get("/sales/summary/by-machine", { params: qp }),
          API.get("/sales/low-stock"),
          API.get("/sales/rankings/machines", { params: qp }),
          API.get("/devices")
        ]);

      let compareData = null;
      if (wantCompare) {
        try {
          const c = await API.get("/sales/kpi-ytd-compare", {
            params: { machine_id: selectedMachine || undefined }
          });
          compareData = c.data;
        } catch {
          compareData = null;
        }
      }

      setKpi(kpiRes.data || {});
      setDaily(Array.isArray(dailyRes.data) ? dailyRes.data : []);
      setMonthly(Array.isArray(monthlyRes.data) ? monthlyRes.data : []);
      setByMachine(Array.isArray(machineRes.data) ? machineRes.data : []);
      setLowStock(Array.isArray(lowStockRes.data) ? lowStockRes.data : []);
      setDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
      setYtdCompare(compareData);
      const rank = rankRes.data || {};
      setMachineRankings({
        top10: Array.isArray(rank.top10) ? rank.top10 : [],
        bottom10: Array.isArray(rank.bottom10) ? rank.bottom10 : []
      });
    } finally {
      if (!firstFetchDoneRef.current) {
        firstFetchDoneRef.current = true;
        setInitializing(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadMachines();
        if (!cancelled) await loadAll();
      } catch {
        if (!cancelled) {
          firstFetchDoneRef.current = true;
          setInitializing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (key, options = {}) => {
    const { reload = true } = options;
    setPreset(key);
    if (key === "all") {
      setStartDate("");
      setEndDate("");
      if (reload) {
        loadAll(undefined, buildQueryParams("", "", selectedMachine));
      }
      return;
    }
    if (key === "custom") {
      return;
    }
    const def = PRESETS[key];
    if (def && typeof def.getRange === "function") {
      const r = def.getRange();
      setStartDate(r.start);
      setEndDate(r.end);
      if (reload) {
        loadAll(undefined, buildQueryParams(r.start, r.end, selectedMachine));
      }
    }
  };

  const onFilterClick = () => {
    loadAll();
  };

  const formatCLP = value =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP"
    }).format(value || 0);

  const truncateLabel = (name, id) => {
    const s = name && String(name).trim() ? String(name) : `#${id}`;
    return s.length > 24 ? `${s.slice(0, 22)}…` : s;
  };

  const filterDescription = () => {
    if (!startDate || !endDate) {
      return "Periodo: todo el historial (ventas y gráficos sin acotar por fecha).";
    }
    return `Periodo: ${formatDateDMY(startDate)} → ${formatDateDMY(endDate)}${
      selectedMachine
        ? ` · máquina filtrada`
        : ""
    }.`;
  };

  const dailyData = {
    labels: daily.map(d => formatDateDMY(d.day)),
    datasets: [
      {
        label: "Ventas diarias (CLP)",
        data: daily.map(d => d.total),
        borderColor: "#3b82f6",
        tension: 0.3
      }
    ]
  };

  const monthlyData = {
    labels: monthly.map(m => formatDateDMY(m.month)),
    datasets: [
      {
        label: "Ventas mensuales (CLP)",
        data: monthly.map(m => m.total),
        backgroundColor: "#10b981"
      }
    ]
  };

  const machineData = {
    labels: byMachine.map(m => m.name),
    datasets: [
      {
        label: "Ventas por máquina (CLP)",
        data: byMachine.map(m => m.total),
        backgroundColor: "#f59e0b"
      }
    ]
  };

  const top10HorizData = {
    labels: machineRankings.top10.map(m => truncateLabel(m.name, m.machine_id)),
    datasets: [
      {
        label: "Ventas (CLP)",
        data: machineRankings.top10.map(m => m.total),
        backgroundColor: "#059669",
        borderRadius: 4
      }
    ]
  };

  const bottom10HorizData = {
    labels: machineRankings.bottom10.map(m => truncateLabel(m.name, m.machine_id)),
    datasets: [
      {
        label: "Ventas (CLP)",
        data: machineRankings.bottom10.map(m => m.total),
        backgroundColor: "#ea580c",
        borderRadius: 4
      }
    ]
  };

  const horizontalBarOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => formatCLP(ctx.raw)
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 8,
          callback: v => {
            const n = Number(v);
            if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
            if (n >= 1e3) return `${Math.round(n / 1e3)}k`;
            return n;
          }
        },
        grid: { color: "#f1f5f9" }
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      }
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true }
    }
  };

  const unitsHint =
    kpi.period_days != null
      ? `${kpi.total_transactions ?? 0} u. ÷ ${kpi.period_days} días del periodo`
      : kpi.units_month != null && kpi.day_of_month != null
        ? `${kpi.units_month} u. mes ÷ día ${kpi.day_of_month}`
        : "Mes calendario o periodo seleccionado.";

  const isFresh = (ts, minutes) => {
    if (!ts) return false;
    const ms = new Date(ts).getTime();
    if (Number.isNaN(ms)) return false;
    return Date.now() - ms <= minutes * 60 * 1000;
  };

  const statusRows = machines.map(m => {
    const deviceForMachine = devices.find(d => Number(d.machine_id) === Number(m.id));
    const machineOnline =
      !!deviceForMachine &&
      !!deviceForMachine.active &&
      isFresh(deviceForMachine.last_seen_at, 15);
    const qibixxOnline =
      !!deviceForMachine &&
      !!deviceForMachine.qibixx_usb_connected &&
      isFresh(deviceForMachine.qibixx_last_report_at, 5);
    return {
      machineId: m.id,
      machineName: m.name,
      deviceName: deviceForMachine?.name || "Sin terminal",
      machineOnline,
      qibixxOnline,
      lastSeenAt: deviceForMachine?.last_seen_at || null,
      qibixxLastReportAt: deviceForMachine?.qibixx_last_report_at || null
    };
  });

  const dotStyle = isOn => ({
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    display: "inline-block",
    marginRight: "6px",
    backgroundColor: isOn ? "var(--color-success)" : "var(--color-danger)"
  });

  if (initializing) {
    return (
      <div className="dashboard-skeleton-wrap" aria-busy="true" aria-live="polite">
        <header className="page-header">
          <div className="skeleton skeleton--h1" />
          <div className="skeleton skeleton--line wide" />
        </header>
        <div className="skeleton skeleton--card-block" />
        <div className="dashboard-kpi-grid" style={{ marginBottom: "1.75rem" }}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="skeleton skeleton--kpi" />
          ))}
        </div>
        <div className="skeleton skeleton--chart" />
      </div>
    );
  }

  return (
    <div className={refreshing ? "dashboard-root dashboard-root--refreshing" : "dashboard-root"}>
      {refreshing ? (
        <div className="dashboard-refresh-bar" role="status" aria-label="Actualizando datos" />
      ) : null}
      <header className="page-header">
        <h1 className="page-title">Dashboard ejecutivo</h1>
        <p className="page-subtitle">
          Ventas, métricas y rankings. Usá los filtros para acotar por fechas y máquina; el
          comparativo muestra el año en curso vs el mismo periodo del año anterior (YTD).
        </p>
      </header>

      <section className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <h2 className="section-title" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
          Accesos rápidos
        </h2>
        <div className="home-quick-grid">
          <Link to="/soporte-panel" className="home-quick-card home-quick-card--accent">
            <strong>Soporte QR</strong>
            <span>Tickets, menú cliente y URLs por máquina</span>
          </Link>
          <Link to="/control" className="home-quick-card">
            <strong>Control operativo</strong>
            <span>Semáforo por terminal, USB, MDB y alertas</span>
          </Link>
          <Link to="/fleet" className="home-quick-card">
            <strong>Flota</strong>
            <span>Terminales Android y salud de red</span>
          </Link>
          <Link to="/qibixx" className="home-quick-card">
            <strong>Monitor Qibixx</strong>
            <span>Telemetría USB y parseo MDB</span>
          </Link>
        </div>
      </section>

      <div className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <h2 className="section-title" style={{ marginBottom: "0.75rem" }}>
          Filtros
        </h2>
        <p style={{ fontSize: "0.9rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
          {filterDescription()}
        </p>
        <div
          className="field-row"
          style={{ flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}
        >
          <span style={{ fontSize: "0.85rem", alignSelf: "center", marginRight: "0.25rem" }}>
            Presets:
          </span>
          <button
            type="button"
            className={`btn ${preset === "this_month" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => applyPreset("this_month")}
            disabled={refreshing}
          >
            Este mes
          </button>
          <button
            type="button"
            className={`btn ${preset === "this_year" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => applyPreset("this_year")}
            disabled={refreshing}
          >
            Este año
          </button>
          <button
            type="button"
            className={`btn ${preset === "all" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => applyPreset("all")}
            disabled={refreshing}
          >
            Todo el historial
          </button>
          <button
            type="button"
            className={`btn ${preset === "custom" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => {
              setPreset("custom");
            }}
            disabled={refreshing}
          >
            Rango personalizado
          </button>
        </div>
        <div className="toolbar" style={{ flexWrap: "wrap" }}>
          <label style={{ fontSize: "0.85rem", alignSelf: "center" }}>Desde</label>
          <input
            className="input"
            type="date"
            value={startDate}
            onChange={e => {
              setPreset("custom");
              setStartDate(e.target.value);
            }}
            disabled={preset === "all" || refreshing}
          />
          <label style={{ fontSize: "0.85rem", alignSelf: "center" }}>Hasta</label>
          <input
            className="input"
            type="date"
            value={endDate}
            onChange={e => {
              setPreset("custom");
              setEndDate(e.target.value);
            }}
            disabled={preset === "all" || refreshing}
          />
          <select
            className="select"
            value={selectedMachine}
            onChange={e => setSelectedMachine(e.target.value)}
            disabled={refreshing}
          >
            <option value="">Todas las máquinas</option>
            {machines.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onFilterClick}
            disabled={refreshing}
          >
            {refreshing ? "Actualizando…" : "Aplicar filtros"}
          </button>
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginTop: "0.75rem",
            fontSize: "0.9rem",
            cursor: "pointer"
          }}
        >
          <input
            type="checkbox"
            checked={showYearCompare}
            disabled={refreshing}
            onChange={e => {
              const v = e.target.checked;
              setShowYearCompare(v);
              loadAll(v);
            }}
          />
          Mostrar comparativo <strong>año actual vs año anterior</strong> (mismo periodo YTD,
          independiente del rango de fechas de arriba)
        </label>
      </div>

      {showYearCompare && ytdCompare && (
        <div
          className="card card--padded"
          style={{
            marginBottom: "1.25rem",
            borderLeft: "4px solid var(--color-accent, #3b82f6)"
          }}
        >
          <h2 className="section-title">
            Comparativo YTD ({ytdCompare.current_year} vs {ytdCompare.previous_year})
          </h2>
          <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
            Mismo lapso de días: desde el 1 de enero hasta “hoy” en cada año. Respeta el filtro de
            máquina si está seleccionado.
          </p>
          <div className="dashboard-kpi-grid">

            <div className="kpi-card">

              <h3 className="kpi-card__label">{ytdCompare.current_year} (año actual)</h3>

              <p className="kpi-card__value">{formatCLP(ytdCompare.current?.total_sales)}</p>

              <p className="kpi-card__hint">

                {ytdCompare.current?.total_transactions ?? 0} transacciones

              </p>

            </div>

            <div className="kpi-card">

              <h3 className="kpi-card__label">{ytdCompare.previous_year} (año anterior)</h3>

              <p className="kpi-card__value">{formatCLP(ytdCompare.previous?.total_sales)}</p>

              <p className="kpi-card__hint">

                {ytdCompare.previous?.total_transactions ?? 0} transacciones

              </p>

            </div>

            <div className="kpi-card">

              <h3 className="kpi-card__label">Variación ventas</h3>

              <p className="kpi-card__value">

                {ytdCompare.delta_sales_pct != null

                  ? `${ytdCompare.delta_sales_pct >= 0 ? "+" : ""}${Number(

                      ytdCompare.delta_sales_pct

                    ).toLocaleString("es-CL", {

                      minimumFractionDigits: 1,

                      maximumFractionDigits: 2

                    })}%`

                  : "—"}

              </p>

              <p className="kpi-card__hint">Respecto al mismo periodo del año pasado</p>

            </div>

          </div>

        </div>

      )}



      {lowStock.length > 0 && (

        <div className="alert alert--warning">

          <h3>Stock bajo</h3>

          {lowStock.map((item, index) => (

            <p key={index}>

              {item.machine} — {item.product_name} (stock: {item.stock})

            </p>

          ))}

        </div>

      )}

      <div className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <h2 className="section-title">Estado online por máquina</h2>
        <p
          style={{
            fontSize: "0.85rem",
            color: "var(--color-text-muted)",
            marginBottom: "0.85rem"
          }}
        >
          Máquina online = terminal activa vista en los últimos 15 minutos. Qibixx online =
          último reporte USB en los últimos 5 minutos.
        </p>
        <div className="table-wrap">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Máquina</th>
                <th>Terminal</th>
                <th>Estado máquina</th>
                <th>Estado Qibixx</th>
                <th>Última señal máquina</th>
                <th>Último reporte Qibixx</th>
              </tr>
            </thead>
            <tbody>
              {statusRows.map(row => (
                <tr key={row.machineId}>
                  <td>{row.machineName}</td>
                  <td>{row.deviceName}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <span style={dotStyle(row.machineOnline)} />
                    {row.machineOnline ? "Online" : "Offline"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <span style={dotStyle(row.qibixxOnline)} />
                    {row.qibixxOnline ? "Online" : "Offline"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {row.lastSeenAt ? formatDateTimeDMY(row.lastSeenAt) : "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {row.qibixxLastReportAt ? formatDateTimeDMY(row.qibixxLastReportAt) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="section-title">Indicadores (KPI)</h2>

      <div className="dashboard-kpi-grid">

        <div className="kpi-card">

          <h3 className="kpi-card__label">Ventas (dinero)</h3>

          <p className="kpi-card__value">{formatCLP(kpi.total_sales)}</p>

          <p className="kpi-card__hint">CLP según filtro de fechas y máquina.</p>

        </div>

        <div className="kpi-card">

          <h3 className="kpi-card__label">Ventas (unidades)</h3>

          <p className="kpi-card__value">

            {kpi.total_units != null ? kpi.total_units : kpi.total_transactions ?? 0}

          </p>

          <p className="kpi-card__hint">1 venta = 1 unidad (respeta filtro).</p>

        </div>

        <div className="kpi-card">

          <h3 className="kpi-card__label">Unidades / día</h3>

          <p className="kpi-card__value">

            {kpi.units_per_day_month != null

              ? Number(kpi.units_per_day_month).toLocaleString("es-CL", {

                  minimumFractionDigits: 2,

                  maximumFractionDigits: 2

                })

              : "—"}

          </p>

          <p className="kpi-card__hint">{unitsHint}</p>

        </div>

        <div className="kpi-card">

          <h3 className="kpi-card__label">Transacciones</h3>

          <p className="kpi-card__value">{kpi.total_transactions ?? 0}</p>

          <p className="kpi-card__hint">Registros de venta (filtro).</p>

        </div>



        <div className="kpi-card">

          <h3 className="kpi-card__label">Máquinas operativas</h3>

          <p className="kpi-card__value" style={{ fontSize: "1.25rem" }}>

            <span style={{ color: "var(--color-success)" }}>ON {kpi.machines_online ?? 0}</span>

            <span style={{ color: "var(--color-text-faint)", padding: "0 4px" }}>/</span>

            <span style={{ color: "var(--color-text-muted)" }}>OFF {kpi.machines_offline ?? 0}</span>

          </p>

          <p className="kpi-card__hint">ON = dispositivo visto en 15 min.</p>

        </div>

        <div className="kpi-card">

          <h3 className="kpi-card__label">Transacciones fallidas</h3>

          <p

            className="kpi-card__value"

            style={{

              display: "flex",

              alignItems: "baseline",

              justifyContent: "center",

              gap: "8px",

              flexWrap: "wrap",

              fontSize: "1.35rem"

            }}

          >

            <span style={{ fontWeight: 700, color: "var(--color-danger)" }}>

              {kpi.payment_failed_count ?? 0}

            </span>

            <span style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--color-text-muted)" }}>

              {`${Number(kpi.payment_failed_pct ?? 0).toLocaleString("es-CL", {

                minimumFractionDigits: 0,

                maximumFractionDigits: 2

              })}%`}

            </span>

          </p>

          <p className="kpi-card__hint">

            {kpi.payment_attempts_total != null

              ? `${kpi.payment_attempts_total} intentos en periodo`

              : "Telemetría app (payment_started)."}

          </p>

        </div>

        <div className="kpi-card">

          <h3 className="kpi-card__label">Máquinas (total)</h3>

          <p className="kpi-card__value">{kpi.machines ?? 0}</p>

          <p className="kpi-card__hint">Registradas en la empresa.</p>

        </div>

        <div className="kpi-card">

          <h3 className="kpi-card__label">Sin stock</h3>

          <p className="kpi-card__value">{kpi.noStock ?? 0}</p>

          <p className="kpi-card__hint">Slots con stock 0.</p>

        </div>

      </div>



      <h2 className="section-title">Ranking por máquina (ventas en $)</h2>

      <p className="page-subtitle" style={{ marginBottom: "1rem" }}>

        {startDate && endDate

          ? `Periodo del ranking: ${formatDateDMY(startDate)} → ${formatDateDMY(endDate)}.`

          : "Periodo: todo el historial."}

      </p>

      <div className="dashboard-rank-charts">

        <div className="card card--chart kpi-card--left">

          <h3 className="kpi-card__label" style={{ marginBottom: "0.75rem" }}>

            Top 10 — mayor venta

          </h3>

          <div style={{ height: "380px", position: "relative" }}>

            {machineRankings.top10.length > 0 ? (

              <Bar data={top10HorizData} options={horizontalBarOptions} />

            ) : (

              <p style={{ color: "var(--color-text-faint)", fontSize: "0.9rem" }}>

                Sin datos de ventas por máquina.

              </p>

            )}

          </div>

        </div>

        <div className="card card--chart kpi-card--left">

          <h3 className="kpi-card__label" style={{ marginBottom: "0.75rem" }}>

            10 máquinas — menor venta

          </h3>

          <div style={{ height: "380px", position: "relative" }}>

            {machineRankings.bottom10.length > 0 ? (

              <Bar data={bottom10HorizData} options={horizontalBarOptions} />

            ) : (

              <p style={{ color: "var(--color-text-faint)", fontSize: "0.9rem" }}>

                Sin datos de ventas por máquina.

              </p>

            )}

          </div>

        </div>

      </div>



      <h2 className="section-title">Gráficos</h2>

      <div className="dashboard-chart-grid">

        <div className="card card--chart">

          <div className="chart-box">

            <Line data={dailyData} options={chartOptions} />

          </div>

        </div>

        <div className="card card--chart">

          <div className="chart-box">

            <Bar data={monthlyData} options={chartOptions} />

          </div>

        </div>

        <div className="card card--chart">

          <div className="chart-box">

            <Bar data={machineData} options={chartOptions} />

          </div>

        </div>

        <div className="card card--padded">

          <h3 className="kpi-card__label" style={{ marginBottom: "0.75rem", textAlign: "left" }}>

            Resumen por máquina

          </h3>

          {byMachine.map(m => (

            <p key={m.name} style={{ margin: "0.35rem 0", fontSize: "0.9rem" }}>

              <strong>{m.name}</strong> — {formatCLP(m.total)}

            </p>

          ))}

        </div>

      </div>

    </div>

  );

}



export default Dashboard;
